import React, { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { useAuth } from "@/hooks/useAuth"
import { payslipService } from "@/services/payslipService"
import { FileText, Download, RefreshCw, Calendar, User, DollarSign, Eye } from "lucide-react"
import toast from "react-hot-toast"

const MyPayslips: React.FC = () => {
  const { user } = useAuth()
  const [isDownloading, setIsDownloading] = useState<number | null>(null)

  // Fetch payslips
  const { data: payslipsData, isLoading, refetch, isRefetching } = useQuery({
    queryKey: ["myPayslips"],
    queryFn: () => payslipService.getMyPayslips({ page: 1, limit: 10 }),
    staleTime: 5 * 60 * 1000,
  })

  const payslips = payslipsData?.data?.payslips || []
  const totalPayslips = payslips.length
  const latestPayslip = payslips[0]

  const handleDownloadPayslip = async (
    payslipId: number,
    employeeName: string,
    month: string,
    year: number
  ) => {
    setIsDownloading(payslipId)
    try {
      const blob = await payslipService.downloadPayslipPDF(payslipId)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = `${employeeName}_Payslip_${month}_${year}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      toast.success("Payslip downloaded successfully!")
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || "Failed to download payslip")
    } finally {
      setIsDownloading(null)
    }
  }

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })

  const getMonthName = (month: number) =>
    [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ][month - 1] || "Unknown"

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "finalized":
        return "bg-emerald-50 text-emerald-700 border-emerald-200"
      case "processing":
        return "bg-amber-50 text-amber-700 border-amber-200"
      case "draft":
      default:
        return "bg-indigo-50 text-indigo-700 border-indigo-200"
    }
  }

  // Access denied (non-employee)
  if (!user || user.role !== "employee") {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="h-16 w-16 mx-auto bg-indigo-50 rounded-full flex items-center justify-center mb-4">
            <User className="text-indigo-600 h-6 w-6" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Access Denied</h3>
          <p className="text-gray-600 dark:text-gray-400 text-sm mb-6">
            This page is only accessible by employees.
          </p>
          <a
            href="/"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium transition-all duration-300 hover:shadow-lg"
          >
            Go Back to Dashboard
          </a>
        </div>
      </div>
    )
  }

  // Employee view
  return (
    <div className="max-w-7xl mx-auto space-y-5">
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3 mb-4 lg:mb-0">
          <div className="h-10 w-10 rounded-lg bg-indigo-50 flex items-center justify-center">
            <FileText size={20} className="text-indigo-600" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
              My Payslips
            </h1>
            <p className="text-gray-500 dark:text-gray-400 text-xs">
              View and download your monthly payslips and salary statements
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <div className="bg-white dark:bg-gray-800 rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-700">
            <div className="text-gray-500 dark:text-gray-400 text-xs mb-1">Total Payslips</div>
            <div className="text-gray-900 dark:text-white font-medium text-xs flex items-center gap-1">
              <FileText size={12} />
              {totalPayslips}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-700">
            <div className="text-gray-500 dark:text-gray-400 text-xs mb-1">Latest</div>
            <div className="text-gray-900 dark:text-white font-medium text-xs">
              {latestPayslip ?
                `${getMonthName(latestPayslip.payrollRun.payrollMonth)} ${latestPayslip.payrollRun.payrollYear}`
                : "None"
              }
            </div>
          </div>
        </div>
      </div>

      {/* Action Bar */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <DollarSign size={16} className="text-gray-600 dark:text-gray-400" />
          <span className="text-gray-600 dark:text-gray-400 text-sm font-medium">
            {totalPayslips} payslip{totalPayslips !== 1 ? 's' : ''} available
          </span>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isRefetching}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-medium transition-all duration-200 hover:shadow-md bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <RefreshCw size={14} className={isRefetching ? "animate-spin" : ""} />
          {isRefetching ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {/* Payslip List */}
      {isLoading ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card p-8 text-center">
          <div className="h-8 w-8 rounded-full border-[3px] border-indigo-600 border-t-transparent animate-spin mx-auto mb-3" />
          <p className="text-gray-600 dark:text-gray-400 text-sm">Loading your payslips...</p>
        </div>
      ) : payslips.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card p-8 text-center">
          <div className="h-12 w-12 mx-auto bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-3">
            <FileText className="text-gray-400 dark:text-gray-500 h-5 w-5" />
          </div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">No Payslips Found</h3>
          <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
            Your payslips will appear here once payroll is processed.
          </p>
          <button
            onClick={() => refetch()}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg transition-all duration-200 bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            <RefreshCw size={14} />
            Check Again
          </button>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Pay Period</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Date Range</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Status</th>
                  <th className="py-3 px-4 text-right text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {payslips.map((payslip) => {
                  const statusClasses = getStatusBadge(payslip.payrollRun.status)
                  return (
                    <tr key={payslip.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-150">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div className="h-8 w-8 rounded-lg flex items-center justify-center bg-indigo-50 text-indigo-600">
                            <FileText size={14} />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900 dark:text-white text-sm">
                              {getMonthName(payslip.payrollRun.payrollMonth)} {payslip.payrollRun.payrollYear}
                            </h4>
                            <p className="text-gray-500 dark:text-gray-400 text-xs">Payroll Cycle</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                          <Calendar size={12} />
                          <span className="text-xs">
                            {formatDate(payslip.payrollRun.periodStart)} – {formatDate(payslip.payrollRun.periodEnd)}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold border ${statusClasses}`}
                        >
                          {payslip.payrollRun.status.charAt(0).toUpperCase() + payslip.payrollRun.status.slice(1)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-all duration-200 hover:shadow-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            <Eye size={12} />
                            View
                          </button>
                          <button
                            onClick={() =>
                              handleDownloadPayslip(
                                payslip.id,
                                `${user.firstName}_${user.lastName}`,
                                getMonthName(payslip.payrollRun.payrollMonth),
                                payslip.payrollRun.payrollYear
                              )
                            }
                            disabled={isDownloading === payslip.id}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-all duration-200 hover:shadow-sm disabled:opacity-50 bg-indigo-600 hover:bg-indigo-700 text-white"
                          >
                            {isDownloading === payslip.id ? (
                              <>
                                <RefreshCw size={12} className="animate-spin" />
                                ...
                              </>
                            ) : (
                              <>
                                <Download size={12} />
                                PDF
                              </>
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Information Section */}
      {payslips.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
              <FileText size={14} className="text-indigo-600" />
              About Your Payslips
            </h3>
            <p className="text-gray-600 dark:text-gray-400 text-xs mb-3">
              Your payslips contain detailed information about your earnings,
              deductions, and net pay for each pay period.
            </p>
            <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
              <li className="flex items-center gap-1">
                <div className="w-1.5 h-1.5 bg-indigo-600 rounded-full"></div>
                Download payslips for your records
              </li>
              <li className="flex items-center gap-1">
                <div className="w-1.5 h-1.5 bg-indigo-600 rounded-full"></div>
                Keep copies for tax purposes
              </li>
              <li className="flex items-center gap-1">
                <div className="w-1.5 h-1.5 bg-indigo-600 rounded-full"></div>
                Contact HR for any discrepancies
              </li>
            </ul>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
              <Calendar size={14} className="text-indigo-600" />
              Payroll Schedule
            </h3>
            <p className="text-gray-600 dark:text-gray-400 text-xs mb-3">
              Payslips are typically available by the 5th of each month for the
              previous month's payroll.
            </p>
            <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
              <li className="flex items-center gap-1">
                <div className="w-1.5 h-1.5 bg-indigo-600 rounded-full"></div>
                Monthly payroll processing
              </li>
              <li className="flex items-center gap-1">
                <div className="w-1.5 h-1.5 bg-indigo-600 rounded-full"></div>
                Available by 5th of each month
              </li>
              <li className="flex items-center gap-1">
                <div className="w-1.5 h-1.5 bg-indigo-600 rounded-full"></div>
                Contact HR for urgent issues
              </li>
            </ul>
          </div>
        </div>
      )}
    </div>
  )
}

export default MyPayslips
